import { SecondaryButton } from '@src/components/common/dialogStyles';
import { TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import _isEqual from 'lodash/isEqual';
import { observer } from 'mobx-react-lite';
import {
  type Dispatch,
  type SetStateAction,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import PrimaryActionButton from '../PrimaryActionButton';
import { SaveNameModal } from '../SaveNameModal';

import type {
  CreatedTemplateRecord,
  KaraokeTemplate,
  Template,
  TemplateSaveResult,
} from '../../textProcessor/TextBrandingService';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';

type BaseProps = {
  toggleAddNewTemplate: Dispatch<SetStateAction<boolean>>;
  addNewTemplate: boolean;
  setAllowCustomTemplateSave: Dispatch<SetStateAction<boolean>>;
  allowCustomTemplateSave: boolean;
  setTemplateNameError: Dispatch<SetStateAction<boolean>>;
  templateNameError: boolean;
  templateName: string | undefined;
  setTemplateName: Dispatch<SetStateAction<string | undefined>>;
  type: 'basic' | 'karaoke';
  /**
   * When `true`, suppresses the "Update Caption Template" primary button.
   * The secondary "Save Caption Template" button still renders. Used on the
   * Graphics → Text surface, where the "Update" concept is a legacy
   * basic-text-template flow being phased out.
   */
  hideUpdateButton?: boolean;
  /**
   * Fires after a successful `textBrandingService.save(...)`. Lets the
   * caller react to the save — e.g. navigate the user to the surface
   * where the freshly-saved template lives. Not fired when the save
   * fails: the user stays put, with their changes intact, to retry.
   */
  onAfterSave?: () => void;
};

type KaraokeSettingsProps = BaseProps & {
  setting: 'karaoke-settings';
  config: KaraokeConfig;
  templates: Template[];
};

type BasicSettingsProps = BaseProps & {
  setting: 'text-settings';
  config?: never;
  templates?: never;
};

type Props = KaraokeSettingsProps | BasicSettingsProps;
const keysToRemove = ['id', 'parent_template_id', 'title'];

type SaveAction = 'create' | 'update';

/**
 * Shared by both buttons: a save is already running, so a second one can't
 * start. Says what unlocks the action rather than restating that it is off,
 * per the design system's disabled-row spec.
 */
const SAVE_IN_FLIGHT_REASON = 'Wait for the current save to finish';

const TOOLTIP_ID = 'caption-template-actions-disabled';

const KaraokeSaveOrCancelTemplate = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const [modalAction, setModalAction] = useState<null | SaveAction>(null);
  // PLAT-11653 — which save this component itself has in flight. Drives the
  // loading label / aria-busy on the button that was clicked and blocks a
  // double submit. Local rather than `textBrandingService.isLoading` so a
  // save started elsewhere (TextSettings' inline Update) dims the buttons
  // without relabelling one of them.
  const [pendingAction, setPendingAction] = useState<null | SaveAction>(null);
  // The same flag as `pendingAction`, kept on a ref because the Retry action
  // handed to the error toast keeps the closure of the render that started
  // the failed save — where the state binding is forever the pre-save `null`.
  // Reading state there would let repeated Retry clicks run concurrent saves,
  // which on the create path means duplicate template records.
  const savingRef = useRef(false);
  const textBrandingService = videoCreator.textBrandingService;

  const isPresetTemplate = textBrandingService.isPresetTemplate(
    textBrandingService.selectedTemplateId,
  );

  // PLAT-11416 — while the captions are in a non-source language, "Update"
  // actually creates that language's variant of the template family (the
  // save() branch); the label says so. Same bound-record derivation as
  // save() so label and behavior can't disagree.
  const variantLanguageKey =
    props.setting === 'karaoke-settings' && !isPresetTemplate
      ? textBrandingService.getCaptionVariantKeyForSave(
          (textBrandingService.modifiedTemplateData?.template?.id ||
            textBrandingService.selectedTemplateId) as string,
        )
      : null;
  const variantLanguageLabel = variantLanguageKey
    ? formatLanguageCaption(variantLanguageKey.split('-')[0])
    : null;
  const updateButtonLabel = variantLanguageLabel
    ? `Save ${variantLanguageLabel} Variant`
    : 'Update Caption Template';

  /**
   * Copy for the pending → outcome toast (design system "Notification
   * Toasts → Long-running actions"). The update path has three faces:
   * overwrite the template, create a language variant of it, or (from Save
   * Caption Template) create a new one — the toast names the one that ran.
   */
  const toastCopy = (action: SaveAction, templateName: string) => {
    if (action === 'create') {
      return {
        pending: 'Saving caption template…',
        success: `Caption template "${templateName}" saved`,
        error: "Couldn't save the caption template. Please try again.",
      };
    }
    if (variantLanguageLabel) {
      return {
        pending: `Saving ${variantLanguageLabel} variant…`,
        success: `${variantLanguageLabel} variant saved`,
        error: `Couldn't save the ${variantLanguageLabel} variant. Please try again.`,
      };
    }
    return {
      pending: 'Updating caption template…',
      success: 'Caption template updated',
      error: "Couldn't update the caption template. Please try again.",
    };
  };

  /**
   * The one reason this action is blocked, or `null` when it is available.
   * Several can apply at once — an unsaved story with nothing modified — so
   * they resolve in order of what the user has to deal with first, the way
   * `HighlightTogglePill` resolves its pill reasons.
   *
   * A save this component started is *busy*, not disabled: the button that
   * was clicked keeps its own label ("Updating…") and gets no tooltip, while
   * the other one is blocked and explains itself.
   */
  const disabledReasonFor = (action: SaveAction): string | null => {
    if (pendingAction) {
      return pendingAction === action ? null : SAVE_IN_FLIGHT_REASON;
    }
    if (videoCreator.textBrandingService.isLoading) {
      return SAVE_IN_FLIGHT_REASON;
    }
    if (!videoCreator.organization) {
      return action === 'create'
        ? 'Add this story to a showcase to save caption templates'
        : 'Add this story to a showcase to update caption templates';
    }
    const modified =
      action === 'create' || props.type === 'karaoke'
        ? isTemplateModified
        : videoCreator.textBrandingService.isTemplateModified;
    if (!modified) {
      if (action === 'create') {
        return 'Change a caption setting to save it as a template';
      }
      return variantLanguageLabel
        ? `Change a caption setting to save a ${variantLanguageLabel} variant`
        : 'Change a caption setting to update this template';
    }
    return null;
  };

  const handleSaveTemplate = async (
    templateName: string,
    action: SaveAction,
    // The record the previous attempt already created, when it got that
    // far — carried into the retry so it writes into that record instead
    // of creating a second one.
    resume?: CreatedTemplateRecord,
  ) => {
    setModalAction(null);
    if (savingRef.current) return;
    savingRef.current = true;

    const copy = toastCopy(action, templateName);
    // Up before the await so the click is acknowledged immediately; swapped
    // in place for the outcome below on both paths (pending is sticky).
    const toastId = videoCreator.showToast({
      state: 'pending',
      message: copy.pending,
    });

    setPendingAction(action);
    let result: TemplateSaveResult = { ok: false };
    try {
      result = await videoCreator.textBrandingService.save(
        templateName,
        props.type,
        props.setting,
        undefined,
        action,
        resume,
      );
    } finally {
      savingRef.current = false;
      setPendingAction(null);
    }

    if (!result.ok) {
      const created = result.created;
      videoCreator.updateToast(toastId, {
        state: 'error',
        message: copy.error,
        action: {
          label: 'Retry',
          onClick: () => void handleSaveTemplate(templateName, action, created),
        },
      });
      return;
    }

    videoCreator.updateToast(toastId, {
      state: 'success',
      message: copy.success,
    });

    props.setTemplateName('');
    props.toggleAddNewTemplate(false);
    props.setAllowCustomTemplateSave(false);

    // Fire after local-state cleanup so any navigation that follows
    // doesn't race a stale "preparing to save" flag.
    props.onAfterSave?.();
  };

  const isTemplateModified = useMemo(() => {
    if (!props.config || !props.templates) return true;
    const currentSelectedTemplate = props.templates.find(
      t => t.id === props.config.parent_template_id,
    );
    if (!currentSelectedTemplate || currentSelectedTemplate.type !== 'karaoke')
      return true;
    const config = props.config;
    const selectedTemplateDefaultConfig =
      videoCreator.textBrandingService.transformKaraokeTemplateIntoConfig(
        currentSelectedTemplate as KaraokeTemplate,
      );

    const keys = Object.keys(config).filter(key => !keysToRemove.includes(key));
    const hasValidModification = keys.some(
      key =>
        !_isEqual(
          config[key as keyof KaraokeConfig],
          selectedTemplateDefaultConfig?.[key as keyof Template],
        ),
    );

    return hasValidModification;
  }, [props.config, props.templates]);

  const createDisabledReason = disabledReasonFor('create');
  const updateDisabledReason = disabledReasonFor('update');

  return (
    <>
      <StickyWrapper>
        {videoCreator.textBrandingService.prepareTemplateToSave && (
          <CancelButton
            onClick={() => {
              props.setAllowCustomTemplateSave(false);
              props.toggleAddNewTemplate(false);
              if (videoCreator.textBrandingService.isTemplateModified) {
                videoCreator.textBrandingService.isTemplateModified = false;
                videoCreator.textBrandingService.modifiedTemplateData = null;
                videoCreator.textBrandingService.isPropsChanged = true;
              }

              videoCreator.textBrandingService.prepareTemplateToSave = false;
            }}
          >
            Cancel
          </CancelButton>
        )}
        {/* aria-disabled, never the native `disabled` attribute: a natively
            disabled button takes no pointer events, so the tooltip explaining
            WHY it is inert could never fire. The click is guarded here rather
            than with `pointer-events: none`, which would kill the tooltip the
            same way. See the design system's "Disabled row spec".

            A busy button is aria-disabled too — it ignores activation, so
            assistive tech should not offer it — but it carries no tooltip:
            its own "Saving…" label already says what is happening. */}
        <SaveNewTemplate
          aria-disabled={
            createDisabledReason || pendingAction === 'create'
              ? true
              : undefined
          }
          aria-busy={pendingAction === 'create' || undefined}
          data-tooltip-id={createDisabledReason ? TOOLTIP_ID : undefined}
          data-tooltip-content={createDisabledReason ?? undefined}
          onClick={() => {
            if (createDisabledReason || pendingAction) return;
            setModalAction('create');
          }}
        >
          {pendingAction === 'create' ? 'Saving…' : 'Save Caption Template'}
        </SaveNewTemplate>
        {!props.hideUpdateButton &&
          !videoCreator.textBrandingService.prepareTemplateToSave &&
          !isPresetTemplate && (
            <SaveButton
              aria-disabled={
                updateDisabledReason || pendingAction === 'update'
                  ? true
                  : undefined
              }
              aria-busy={pendingAction === 'update' || undefined}
              data-tooltip-id={updateDisabledReason ? TOOLTIP_ID : undefined}
              data-tooltip-content={updateDisabledReason ?? undefined}
              onClick={() => {
                if (updateDisabledReason) return;
                void handleSaveTemplate(props.templateName ?? '', 'update');
              }}
            >
              {pendingAction === 'update'
                ? variantLanguageLabel
                  ? 'Saving…'
                  : 'Updating…'
                : updateButtonLabel}
            </SaveButton>
          )}
        {/* One tooltip serves both buttons — each supplies its own reason
            through `data-tooltip-content`, and only a blocked button carries
            the id, so an available action shows no hover hint. Replaces the
            hand-rolled absolutely-positioned "needs a showcase" hint, whose
            `:disabled:hover` CSS hooks can no longer match now that the
            buttons are aria-disabled. */}
        <Tooltip
          id={TOOLTIP_ID}
          style={TOOLTIP_STYLE}
          place="top"
          positionStrategy="fixed"
        />
      </StickyWrapper>
      {modalAction && (
        <SaveNameModal
          headingText="Save Caption Template"
          closeModal={() => setModalAction(null)}
          onConfirm={templateName => handleSaveTemplate(templateName, 'create')}
          onCancel={() => setModalAction(null)}
          confirmText="Save"
          cancelText="Cancel"
          initialName={
            videoCreator.textBrandingService.prepareTemplateToSave
              ? ''
              : videoCreator.textBrandingService.selectedBrandingTemplate?.title?.toString() ||
                ''
          }
        />
      )}
    </>
  );
});

export default KaraokeSaveOrCancelTemplate;

/**
 * Cancel is a secondary action, so it derives from SecondaryButton rather than
 * PrimaryActionButton.
 *
 * It used to be `styled(PrimaryActionButton)` repainted with a gold border and
 * gold text — the accent-outline twin of the green-outline pattern the design
 * system names and says to neutralise ("Cancel / secondary buttons are neutral").
 * Gold is a particularly bad choice in this rail, where it already means
 * "selected". Inheriting from the primary also carried its padding-derived height,
 * bold weight and missing hover state.
 *
 * SecondaryButton supplies transparent / --border / --foreground with a
 * --surface-elevated hover, at --btn-h-lg (40px) — the height this was already
 * hardcoding. Only the full-width rail silhouette is added.
 */
export const CancelButton = styled(SecondaryButton)`
  width: 100%;
`;

/**
 * Secondary action per the design-system Button Hierarchy — "save this
 * style as a reusable caption template" is a real supporting action, not a
 * toggle, so it takes the outline treatment rather than the ghost one it
 * used to have (PLAT-11653). Inert states key off `aria-disabled`, not the
 * native attribute, so the tooltip carrying the reason still fires. Derives from the shared `SecondaryButton`
 * (transparent / --border / --foreground, --surface-elevated hover, dimmed
 * when disabled); only the size and the sibling hint hooks are added.
 *
 * Sized "sm" to match the primary below it, so the pair reads as one row
 * of controls rather than two unrelated buttons.
 */
export const SaveNewTemplate = styled(SecondaryButton)`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  font-size: var(--text-xs);

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  /* Inert but still hoverable, so the tooltip carrying the reason can fire.
     The base's hover rule keys off :not(:disabled), which an aria-disabled
     button passes — so the hover feedback is suppressed here instead. */
  &[aria-disabled='true'] {
    opacity: 0.6;
    color: var(--text-muted);
    cursor: not-allowed;
  }
  &[aria-disabled='true']:hover,
  &[aria-disabled='true']:active {
    background: transparent;
    border-color: var(--border);
    color: var(--text-muted);
  }
  &[aria-busy='true'] {
    cursor: progress;
  }
`;

/**
 * Small primary CTA per the design-system Button Hierarchy "sm" size, with
 * the full state set: default, hover, active, focus-visible, disabled (via
 * `aria-disabled`, so the reason tooltip can fire) and loading (the label
 * swaps and `aria-busy` is set while a save is in flight).
 *
 * The base `PrimaryActionButton` is opinionated about being full-width
 * (`width: 100%; flex: 1`) and uses bold weight + always-dimmed opacity.
 * Those defaults don't match a sized-to-content small primary, so we
 * override here:
 *   - width: auto + flex: 0 0 auto → content-sized
 *   - height / padding / font-size → "sm" tokens from the DS
 *   - font-weight → semi-bold per the DS spec
 *   - opacity reset to 1 (only dim on :disabled per the DS Primary rule)
 *   - hover / active use `--primary-hover` / `--primary-pressed` tokens
 *   - focus-visible ring in `--accent`, matching the secondary beside it
 *
 * Reference: docs/design-system.md "Button Hierarchy → Primary" + "sm".
 */
export const SaveButton = styled(PrimaryActionButton)`
  width: auto;
  flex: 0 0 auto;
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  border-radius: var(--radius-button);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  opacity: 1;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 120ms ease,
    opacity 120ms ease;

  background-color: var(--primary);
  color: var(--background);
  border: none;

  &:hover:not(:disabled):not([aria-disabled='true']) {
    background-color: var(--primary-hover);
  }
  &:active:not(:disabled):not([aria-disabled='true']) {
    background-color: var(--primary-pressed);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  /* Inert via aria-disabled rather than the native attribute, so the button
     keeps taking pointer events and the tooltip explaining why can fire. */
  &[aria-disabled='true'] {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &[aria-busy='true'] {
    cursor: progress;
  }
`;

const StickyWrapper = styled.div`
  margin-top: auto;
  padding-top: 20px;
  background: var(--background);
  z-index: 2;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  /*
   * Stack vertically so "Update Caption Template" (small primary CTA)
   * sits below "Save Caption Template" (small secondary). Left-aligned so
   * both buttons hug the panel's left edge — keeps the eye anchored
   * to the start of each row instead of forcing it to the right.
   */
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
`;

export const ChoiceRadio = styled.div<{ isChecked: boolean }>`
  border-radius: 80px;
  border: 1px solid var(--border);
  background: ${props =>
    props.isChecked ? 'var(--accent)' : 'rgba(3, 4, 25, 0.4)'};
  width: 14px;
  height: 14px;
`;

export const SaveCustomAsTemplate = styled.div<{ type: 'basic' | 'karaoke' }>`
  display: flex;
  align-items: center;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: 21px;
  gap: 10px;
  margin: ${props => props.type === 'karaoke' && '15px 0'};
`;

export const NoShowcaseMessage = styled.div`
  position: absolute;
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  border-radius: var(--radius-input);
  background: var(--border);
  top: 0;

  display: none;
  width: 258px;
  height: 42px;
  padding: 2px 8px;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;
